/**
 * AuditCardExpandedContent
 *
 * Renders the expanded detail section of an AuditRuleCard:
 * reason, rule description (markdown), facets, affected sections, source link.
 */

import React from 'react';
import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import { Box, Chip, Typography } from '@mui/material';

import type { AuditCardFacets, AuditCardSeverity, AuditRuleCardData } from './AuditRuleCard.types';

interface AuditCardExpandedContentProps {
  data: AuditRuleCardData;
  onSectionClick?: (sectionId: string) => void;
}

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------

function ReasonSection({ reason, severity }: { reason: string; severity: AuditCardSeverity }) {
  const isWarning = severity === 'warning';
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: isWarning ? 'rgba(255, 152, 0, 0.08)' : 'rgba(235, 74, 38, 0.06)',
        borderLeft: 3,
        borderColor: isWarning ? 'warning.main' : 'error.main',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}
      >
        REASON
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
        {reason}
      </Typography>
    </Box>
  );
}

function FacetsSection({ facets }: { facets: AuditCardFacets }) {
  const hasPayer = !!facets.payer?.length;
  const hasCpt = !!facets.cpt?.length;
  const hasProviderType = !!facets.providerType?.length;

  if (!hasPayer && !hasCpt && !hasProviderType) return null;

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}
      >
        Applies to
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {facets.payer?.map(payer => (
          <Chip
            key={payer}
            label={payer}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ))}
        {facets.cpt?.map(cpt => (
          <Chip
            key={cpt}
            label={`CPT ${cpt}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ))}
        {facets.providerType?.map(type => (
          <Chip
            key={type}
            label={type}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ))}
      </Box>
    </Box>
  );
}

function AffectedSections({
  sections,
  onSectionClick,
}: {
  sections: string[];
  onSectionClick?: (sectionId: string) => void;
}) {
  return (
    <Box>
      <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
        Affects:{' '}
      </Typography>
      {sections.map((sectionId, i) => (
        <React.Fragment key={sectionId}>
          {i > 0 && (
            <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
              ,{' '}
            </Typography>
          )}
          <Typography
            variant="caption"
            component="span"
            onClick={() => onSectionClick?.(sectionId)}
            sx={{
              color: 'primary.main',
              cursor: onSectionClick ? 'pointer' : 'default',
              '&:hover': onSectionClick ? { textDecoration: 'underline' } : undefined,
            }}
          >
            {sectionId}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export const AuditCardExpandedContent: React.FC<AuditCardExpandedContentProps> = ({
  data,
  onSectionClick,
}) => {
  const hasFacets =
    !!data.facets?.payer?.length ||
    !!data.facets?.cpt?.length ||
    !!data.facets?.providerType?.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
      {/* Reason */}
      {data.reason && <ReasonSection reason={data.reason} severity={data.severity} />}

      {/* Rule description (markdown) */}
      {data.bodyMd && (
        <Box sx={{ '& p': { mb: 1 }, '& p:last-child': { mb: 0 } }}>
          <SimpleMarkdownRenderer markdown={data.bodyMd} />
        </Box>
      )}

      {/* Facets */}
      {hasFacets && data.facets && <FacetsSection facets={data.facets} />}

      {/* Affected sections */}
      {!!data.relevantSections?.length && (
        <AffectedSections sections={data.relevantSections} onSectionClick={onSectionClick} />
      )}
    </Box>
  );
};
